import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { getQuestionsByExamId } from '../utils/questionAPI';
import { getExamById } from '../utils/examAPI';
import { getLatestSubjectHistory } from '../utils/subjectHistorysAPI';

function ExamResult({ session }) {
  const { examId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // 从location.state获取考试结果数据，使用useMemo避免每次渲染都创建新对象
  const resultData = useMemo(() => location.state || {}, [location.state]);
  const { score = 0, totalQuestions = 0, correctCount = 0, questionTimes = {} } = resultData;
  
  useEffect(() => {
    const fetchExamInfo = async () => {
      try {
        setLoading(true);
        
        // 检查用户身份
        if (!session && !localStorage.getItem('user_id')) {
          navigate('/login');
          return;
        }
        
        // 如果没有从location传入的结果数据，尝试从数据库获取最新考试记录
        if (Object.keys(resultData).length === 0) {
          const userId = session?.user?.id || localStorage.getItem('user_id');
          
          if (userId) {
            // 尝试从subject_historys表获取最新记录
            const historyData = await getLatestSubjectHistory(userId, examId);
              
            if (historyData) {
              // 更新结果数据
              resultData.score = historyData.score;
              resultData.totalQuestions = historyData.total_questions;
              resultData.correctCount = historyData.correct_count;
              resultData.questionTimes = historyData.question_times || {};
            }
          }
        }
        
        // 获取考试信息
        const examData = await getExamById(examId);
        
        if (examData) {
          setExam(examData);
          
          // 获取考试题目
          const questionsData = await getQuestionsByExamId(examData._id);
          setQuestions(questionsData || []);
        }
        
      } catch (error) {
        console.error('Error fetching exam result data:', error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchExamInfo();
  }, [examId, session, navigate, resultData]);

  // 根据分数获取反馈
  const getFeedback = (score) => {
    if (score >= 90) return { message: '太棒了！你几乎完美地掌握了所有知识点！', color: 'text-green-500' };
    if (score >= 80) return { message: '很好！你对大部分知识点都有很好的理解。', color: 'text-green-500' };
    if (score >= 70) return { message: '不错！你的表现超过了平均水平。', color: 'text-blue-500' };
    if (score >= 60) return { message: '及格！你掌握了基本知识，但还有提升空间。', color: 'text-blue-500' };
    return { message: '继续努力！多多练习，相信你下次会有更好的表现。', color: 'text-red-500' };
  };
  
  const feedback = getFeedback(score);
  
  // 计算平均用时
  const getAverageTime = () => {
    if (!questionTimes || Object.keys(questionTimes).length === 0) return 0;
    const totalTime = Object.values(questionTimes).reduce((sum, time) => sum + time, 0);
    return Math.round(totalTime / Object.keys(questionTimes).length);
  };
  
  // 获取最慢的几道题
  const getSlowestQuestions = (count = 3) => {
    if (!questionTimes || Object.keys(questionTimes).length === 0) return [];
    
    const entries = Object.entries(questionTimes).map(([id, time]) => ({ id, time }));
    entries.sort((a, b) => b.time - a.time);
    
    return entries.slice(0, count).map(entry => ({
      ...entry,
      question: questions.find(q => q._id === entry.id || q.id === entry.id) || {}
    }));
  };
  
  // 根据用时分析学习建议
  const getTimeBasedSuggestion = () => {
    const avgTime = getAverageTime();
    
    if (avgTime < 10) return '你的答题速度很快，但要确保准确性。建议在选择答案前仔细检查。';
    if (avgTime < 20) return '你的答题节奏很好，能够在合理的时间内完成题目。';
    return '你的答题速度较慢，可能需要加强对基础知识的掌握，提高解题效率。';
  };
  
  const goToReview = () => {
    // 可以导航到一个新页面查看答题详情
    navigate('/mistakes');
  };
  
  const goToHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header title="考试结果" />
      
      <div className="bg-white px-4 py-3 mb-4 shadow-sm">
        <div className="flex justify-between">
          <button 
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-3 rounded-md ${activeTab === 'overview' ? 'bg-primary text-white' : 'text-gray-600'}`}
          >
            概览
          </button>
          <button 
            onClick={() => setActiveTab('details')}
            className={`py-2 px-3 rounded-md ${activeTab === 'details' ? 'bg-primary text-white' : 'text-gray-600'}`}
          >
            详细分析
          </button>
          <button 
            onClick={() => setActiveTab('suggestions')}
            className={`py-2 px-3 rounded-md ${activeTab === 'suggestions' ? 'bg-primary text-white' : 'text-gray-600'}`}
          >
            学习建议
          </button>
        </div>
      </div>
      
      <div className="flex-1 p-5">
        {activeTab === 'overview' && (
          <div className="bg-white rounded-2xl shadow-md p-5 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">
              {exam?.name || '考试'}
            </h2>
            
            <div className="my-8 flex flex-col items-center">
              <div className="relative">
                <svg className="w-40 h-40" viewBox="0 0 36 36">
                  <path
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#eee"
                    strokeWidth="3"
                  />
                  <path
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke={score >= 60 ? "#4ade80" : "#f87171"}
                    strokeWidth="3"
                    strokeDasharray={`${score}, 100`}
                    strokeLinecap="round"
                  />
                  <text x="18" y="20.5" textAnchor="middle" fontSize="10" fill="#333" fontWeight="bold">
                    {score}分
                  </text>
                </svg>
                
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="text-5xl font-bold text-gray-800">{score}</div>
                  <div className="text-sm text-center text-gray-500">总分: 100</div>
                </div>
              </div>
              
              <p className={`mt-4 text-center font-medium ${feedback.color}`}>
                {feedback.message}
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-xl">
                <div className="text-blue-800 text-sm">答题总数</div>
                <div className="text-2xl font-bold text-blue-600 mt-1">{totalQuestions}</div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-xl">
                <div className="text-green-800 text-sm">正确题数</div>
                <div className="text-2xl font-bold text-green-600 mt-1">{correctCount}</div>
              </div>
              
              <div className="bg-red-50 p-4 rounded-xl">
                <div className="text-red-800 text-sm">错误题数</div>
                <div className="text-2xl font-bold text-red-600 mt-1">{totalQuestions - correctCount}</div>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-xl">
                <div className="text-purple-800 text-sm">正确率</div>
                <div className="text-2xl font-bold text-purple-600 mt-1">
                  {totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0}%
                </div>
              </div>
            </div>
            
            <div className="flex space-x-4">
              <button
                onClick={goToReview}
                className="flex-1 py-3 rounded-xl bg-white text-primary-dark font-medium shadow-md hover:shadow-lg"
              >
                查看错题
              </button>
              
              <button
                onClick={goToHome}
                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-primary-dark to-primary text-white font-medium shadow-md hover:shadow-lg"
              >
                返回首页
              </button>
            </div>
          </div>
        )}

        {activeTab === 'details' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-md p-5">
              <h3 className="font-bold text-lg text-gray-800 mb-4">用时分析</h3>
              
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-blue-50 p-3 rounded-xl text-center">
                  <div className="text-sm text-blue-800">平均用时</div>
                  <div className="text-xl font-bold text-blue-600">{getAverageTime()}秒</div>
                </div>
                
                <div className="bg-green-50 p-3 rounded-xl text-center">
                  <div className="text-sm text-green-800">总用时</div>
                  <div className="text-xl font-bold text-green-600">
                    {Object.values(questionTimes).reduce((sum, time) => sum + time, 0)}秒
                  </div>
                </div>
                
                <div className="bg-yellow-50 p-3 rounded-xl text-center">
                  <div className="text-sm text-yellow-800">效率评分</div>
                  <div className="text-xl font-bold text-yellow-600">
                    {getAverageTime() < 15 ? 'A' : getAverageTime() < 30 ? 'B' : 'C'}
                  </div>
                </div>
              </div>
              
              <h4 className="font-medium text-gray-800 mb-2">用时最长的题目</h4>
              <div className="space-y-3">
                {getSlowestQuestions().map((item, index) => (
                  <div key={item.id} className="p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-start">
                      <div className="bg-red-100 text-red-600 rounded-full w-6 h-6 flex items-center justify-center font-medium mr-2 flex-shrink-0">
                        {index + 1}
                      </div>
                      <div>
                        <div className="text-sm text-gray-800 mb-1">{item.question.title}</div>
                        <div className="text-xs text-gray-500">用时: {item.time}秒</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-white rounded-2xl shadow-md p-5">
              <h3 className="font-bold text-lg text-gray-800 mb-4">知识点掌握情况</h3>
              
              <div className="space-y-3">
                {questions.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-800 mb-2">按难度分布</h4>
                    <div className="h-4 bg-gray-100 rounded-full overflow-hidden flex">
                      <div className="bg-green-500 h-full" style={{ width: '40%' }}></div>
                      <div className="bg-yellow-500 h-full" style={{ width: '35%' }}></div>
                      <div className="bg-red-500 h-full" style={{ width: '25%' }}></div>
                    </div>
                    <div className="flex text-xs text-gray-600 mt-1 justify-between">
                      <span>容易题 (40%)</span>
                      <span>中等题 (35%)</span>
                      <span>困难题 (25%)</span>
                    </div>
                  </div>
                )}
                
                <h4 className="font-medium text-gray-800 mb-2">错题分析</h4>
                {questions.length > 0 && resultData.answers ? (
                  questions.filter(q => {
                    const userAnswer = resultData.answers[q._id] || '';
                    const correctOption = q.options.find(opt => opt.value === "1");
                    return correctOption && userAnswer !== correctOption.code;
                  }).map((question, index) => (
                    <div key={question._id} className="p-3 border border-gray-200 rounded-lg">
                      <div className="flex items-start">
                        <div className="bg-red-100 text-red-600 rounded-full w-6 h-6 flex items-center justify-center font-medium mr-2 flex-shrink-0">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-800 mb-1">{question.title}</div>
                          <div className="text-xs text-gray-500 mb-2">
                            <span className="text-red-500">你的答案: {resultData.answers[question._id] || '未作答'}</span>
                            {' | '}
                            <span className="text-green-500">正确答案: {question.options.find(opt => opt.value === "1")?.code}</span>
                          </div>
                          <div className="text-xs bg-yellow-50 p-2 rounded-lg text-yellow-700">
                            {question.comments || '暂无解析'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <p>暂无错题数据</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'suggestions' && (
          <div className="bg-white rounded-2xl shadow-md p-5 mb-6">
            <h3 className="font-bold text-lg text-gray-800 mb-4">学习建议</h3>
            
            <div className="space-y-5">
              <div className="bg-blue-50 p-4 rounded-xl">
                <h4 className="font-medium text-blue-800 mb-1">成绩分析</h4>
                <p className="text-sm text-blue-600">
                  {score >= 80 
                    ? '你的得分很高，说明你对大部分知识点都有很好的掌握。' 
                    : score >= 60 
                      ? '你已经达到了及格线，但仍有提升空间。' 
                      : '你的得分低于及格线，需要加强学习基础知识。'}
                </p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-xl">
                <h4 className="font-medium text-green-800 mb-1">答题时间分析</h4>
                <p className="text-sm text-green-600">
                  {getTimeBasedSuggestion()}
                </p>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-xl">
                <h4 className="font-medium text-yellow-800 mb-1">薄弱环节</h4>
                <p className="text-sm text-yellow-600 mb-2">
                  根据答题情况，你需要重点关注以下知识点：
                </p>
                <ul className="text-sm text-yellow-700 pl-4 list-disc space-y-1">
                  {questions.filter(q => {
                    const userAnswer = resultData.answers?.[q._id] || '';
                    const correctOption = q.options.find(opt => opt.value === "1");
                    return correctOption && userAnswer !== correctOption.code;
                  }).slice(0, 3).map((q, idx) => (
                    <li key={idx}>{q.title.substring(0, 30)}...</li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-xl">
                <h4 className="font-medium text-purple-800 mb-1">推荐学习计划</h4>
                <p className="text-sm text-purple-600 mb-2">
                  根据你的学习情况，我们为你制定了以下学习计划：
                </p>
                <ol className="text-sm text-purple-700 pl-4 list-decimal space-y-1">
                  <li>每天复习错题本中的2-3道题目</li>
                  <li>每周至少完成一次模拟考试</li>
                  <li>重点关注用时较长的题目类型，提高解题效率</li>
                  <li>定期查看学习报告，跟踪学习进度</li>
                </ol>
              </div>
              
              <div className="bg-red-50 p-4 rounded-xl">
                <h4 className="font-medium text-red-800 mb-1">下一步行动</h4>
                <div className="flex flex-col space-y-2 mt-2">
                  <button 
                    onClick={goToReview}
                    className="w-full py-2 bg-white text-red-500 border border-red-500 rounded-lg text-sm font-medium"
                  >
                    查看错题本
                  </button>
                  <button
                    onClick={() => navigate('/study-report')}
                    className="w-full py-2 bg-white text-green-500 border border-green-500 rounded-lg text-sm font-medium"
                  >
                    查看学习报告
                  </button>
                  <button
                    onClick={() => navigate('/question-bank')}
                    className="w-full py-2 bg-white text-blue-500 border border-blue-500 rounded-lg text-sm font-medium"
                  >
                    继续练习
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ExamResult; 